import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Loader, EmptyState } from "@/components/shared";
import { motion } from "framer-motion";
import * as Icons from "lucide-react";

const STATUS = {
  not_started: { label: "New Quest", color: "#FC633B", bg: "#FFEDD5", icon: "Sparkles" },
  pending: { label: "Pending Review", color: "#EAB308", bg: "#FEF9C3", icon: "Clock" },
  resubmit: { label: "Try Again", color: "#3B82F6", bg: "#E5F0FF", icon: "RefreshCw" },
  approved: { label: "Completed ✓", color: "#22C55E", bg: "#DCFCE7", icon: "CheckCircle2" },
};

const TYPE_ICON = { video: "Video", text: "PenLine", image: "Image", audio: "Mic", mcq: "ListChecks", quiz: "ListChecks", link: "Link", completion: "CheckCircle2", story: "BookOpen", parent: "Users" };

export default function Quests() {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({ queryKey: ["quests"], queryFn: async () => (await api.get("/quests")).data });
  if (isLoading) return <Loader />;

  return (
    <div className="space-y-6" data-testid="quests-page">
      <div>
        <h1 className="font-head text-4xl text-csh-blue">Journey Quests 🎯</h1>
        <p className="text-muted-foreground">Complete missions to earn Story Points and badges!</p>
      </div>
      {(!data || data.length === 0) ? <EmptyState title="No quests yet" text="Your coach will unlock quests soon!" /> : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {data.map((q, i) => {
            const st = STATUS[q.my_status] || STATUS.not_started;
            const TI = Icons[TYPE_ICON[q.submission_type] || "Video"];
            return (
              <motion.div key={q.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                whileHover={{ y: -5 }} data-testid={`quest-card-${q.number}`} onClick={() => navigate(`/app/quests/${q.id}`)}
                className="cursor-pointer rounded-3xl bg-white p-5 shadow-sm border border-black/5 card-pop">
                <div className="flex items-start justify-between">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl gradient-purple">
                    <TI size={22} className="text-white" />
                  </div>
                  <span className="rounded-full px-3 py-1 text-xs font-bold" style={{ color: st.color, backgroundColor: st.bg }}>{st.label}</span>
                </div>
                <div className="mt-3 text-xs font-bold uppercase text-muted-foreground">Quest #{q.number} · Week {q.week}</div>
                <h3 className="font-head text-xl text-csh-blue">{q.name}</h3>
                <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{q.instructions}</p>
                <div className="mt-4 flex items-center gap-3 text-sm font-bold">
                  <span className="flex items-center gap-1 text-csh-orange"><Icons.Star size={15} /> +{q.points} XP</span>
                  <span className="flex items-center gap-1 capitalize text-csh-purple"><Icons.Gauge size={15} /> {q.difficulty}</span>
                  {q.mandatory && <span className="text-red-500">• Required</span>}
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}
